import React, { useRef, useState } from "react";

import { Dialog } from "@headlessui/react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { formatUnitOfMeasure } from "../../../shared/formatUnitOfMeasure";
import { formatDate } from "../../../shared/utility";
import Button from "../../ui/Button";
import OperationalDataTableSkeleton from "../../operational/OperationalDataTableSkeleton";

interface ManufactureNewReceivingItemsTableComponentProps {
  getReceivingAssemblyItemInStockByName: (search: string) => Promise<any>;
  openAssemblyItemPopUpToAddToProduct: (item: any, isEdit: boolean) => void;
  setOpen: (open: any) => void;
}

export default function ManufactureNewReceivingItemsTableComponent(
  props: ManufactureNewReceivingItemsTableComponentProps,
) {
  const {
    getReceivingAssemblyItemInStockByName,
    openAssemblyItemPopUpToAddToProduct,
    setOpen,
  } = props;

  const [emptyText, setEmptyText] = useState("Please search for an item");
  const [searching, setSearching] = useState(false);
  const [receivingAssemblyItems, setReceivingAssemblyItems] = useState<any>({
    data: [],
  });

  const searchRef = useRef<HTMLInputElement>(null);

  const handleSearch = async function () {
    const searchVal = searchRef.current?.value ?? "";
    if (searching || searchVal.length === 0) return;

    setSearching(true);
    setReceivingAssemblyItems({ data: [] });
    try {
      const callResult = await getReceivingAssemblyItemInStockByName(searchVal);
      if (callResult?.success) {
        setReceivingAssemblyItems(callResult.data);
        setEmptyText("We could not find that item.");
      } else {
        setEmptyText("Search failed. Please try again.");
      }
    } catch {
      setEmptyText("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Choose a supplier item
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close supplier item selector"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex rounded-md">
            <div className="relative flex items-stretch flex-grow focus-within:z-10">
              <input
                type="search"
                name="searchInput"
                id="searchInput"
                className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                placeholder="Search by name"
                ref={searchRef}
                disabled={searching}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="-ml-px rounded-l-none"
              aria-label="Search supplier items"
              loading={searching}
              onClick={() => handleSearch()}
            >
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Button>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div id="product-table" className="overflow-hidden card-custom">
            {searching ? (
              <OperationalDataTableSkeleton
                columns={[
                  "BOM name / plu",
                  "Name / Product code",
                  "Serial",
                  "Manufacturer / Supplier",
                  "Date Received",
                  "Available quantity",
                ]}
                rows={5}
              />
            ) : (
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-white">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      BOM name / plu
                    </th>
                    <th
                      scope="col"
                      className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Name / Product code
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Serial
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Manufacturer / Supplier
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Date Received
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                    >
                      Available quantity
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {receivingAssemblyItems.data.length > 0 ? (
                    receivingAssemblyItems.data.map((assemblyItem: any) => (
                      <tr
                        key={assemblyItem.id}
                        className="cursor-pointer"
                        onClick={() => {
                          setOpen({ childComponent: null, open: false });
                          openAssemblyItemPopUpToAddToProduct(
                            assemblyItem,
                            false,
                          );
                        }}
                      >
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {assemblyItem.bom_name} <br />
                          <span className="font-bold">
                            {assemblyItem.bom_plu}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-1.5 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                          {assemblyItem.product_name} <br />
                          <span className="font-bold">{assemblyItem.plu}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {assemblyItem.lot_number}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {assemblyItem.manufacturer_name ===
                          assemblyItem.supplier_name
                            ? assemblyItem.manufacturer_name
                            : `${assemblyItem.manufacturer_name} // ${assemblyItem.supplier_name}`}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(assemblyItem.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                          {assemblyItem.in_stock}{" "}
                          {formatUnitOfMeasure(assemblyItem.unit_of_measure)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center p-2 text-sm">
                        {emptyText}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
